import { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import { Divider } from "primereact/divider";
import { Accordion, AccordionTab } from "primereact/accordion";
import { stateWithCity } from "../model";
import { AutoComplete } from "primereact/autocomplete";

// ─── Constants (Monster RSX values) ──────────────────────────────────────────

const EDUCATION_LEVELS = [
  { label: "Any", value: "" },
  { label: "High School or Equivalent", value: "1" },
  { label: "Certification", value: "2" },
  { label: "Vocational", value: "3" },
  { label: "Associate Degree", value: "4" },
  { label: "Bachelor's Degree", value: "5" },
  { label: "Master's Degree", value: "6" },
  { label: "Doctorate", value: "7" },
  { label: "Professional", value: "8" },
  { label: "Some College Coursework", value: "9" },
  { label: "Vocational HS Diploma", value: "10" },
  { label: "Some High School", value: "11" },
];

const WORK_AUTH_OPTIONS = [
  { label: "Any", value: "" },
  { label: "Authorized to work for any employer", value: "1" },
  { label: "Authorized for current employer only", value: "2" },
  { label: "Requires Sponsorship", value: "3" },
];

const RELOCATION_OPTIONS = [
  { label: "Any", value: "" },
  { label: "Will Relocate", value: "1" },
  { label: "Won't Relocate", value: "0" },
];

const RESUME_UPDATED_OPTIONS = [
  { label: "Any Time", value: "" },
  { label: "Last 1 Day", value: "1" },
  { label: "Last 7 Days", value: "7" },
  { label: "Last 14 Days", value: "14" },
  { label: "Last 30 Days", value: "30" },
  { label: "Last 90 Days", value: "90" },
  { label: "Custom Range", value: "custom" },
];

const RADIUS_OPTIONS = [
  { label: "Exact Location", value: "5" },
  { label: "Within 10 miles", value: "10" },
  { label: "Within 15 miles", value: "15" },
  { label: "Within 20 miles", value: "20" },
  { label: "Within 25 miles", value: "25" },
  { label: "Within 30 miles", value: "30" },
  { label: "Within 35 miles", value: "35" },
  { label: "Within 40 miles", value: "40" },
  { label: "Within 45 miles", value: "45" },
  { label: "Within 50 miles", value: "50" },
  { label: "Within 100 miles", value: "100" },
];

const SALARY_TYPE_OPTIONS = [
  { label: "Any", value: "" },
  { label: "Per Year", value: "1" },
  { label: "Per Hour", value: "2" },
];

const MILITARY_OPTIONS = [
  { label: "Any", value: "" },
  { label: "Active Duty", value: "2" },
  { label: "Veteran/Prior Service", value: "4" },
  { label: "Reservist", value: "5" },
  { label: "Any Military Background", value: "1" },
];
// ─── Default filter state ─────────────────────────────────────────────────────

const DEFAULT_FILTERS = {
  // Search Keywords
  location: [] as any[],
  jobTitle: "",
  radius: "",
  skillKeywords: "",

  // Resume Dates
  resumeUpdatedRange: "",
  resumeUpdatedFrom: null,
  resumeUpdatedTo: null,

  // Candidate Preferences
  relocation: "",
  workAuth: "",
  educationLevel: "",

  // Experience
  mostRecentEmployer: "",
  mostRecentJobTitle: "",
  mostRecentJobDescription: "",

  // Education
  schoolAttended: "",

  // Salary
  salaryMin: "",
  salaryMax: "",
  salaryType: "",

  // Military
  militaryExperience: "",
};

// ─── Field Wrapper ────────────────────────────────────────────────────────────

const FieldWrapper = ({ label, error, children }: any) => (
  <div style={{ marginBottom: "12px" }}>
    {label && (
      <label
        style={{
          display: "block",
          fontSize: "11px",
          fontWeight: 600,
          color: "#374151",
          marginBottom: "4px",
          letterSpacing: "0.03em",
        }}
      >
        {label}
      </label>
    )}
    {children}
    {error && (
      <span style={{ fontSize: "10px", color: "#dc2626", marginTop: "2px", display: "block" }}>
        <i className="pi pi-exclamation-circle" style={{ marginRight: 3 }} />
        {error}
      </span>
    )}
  </div>
);

// ─── Shared input style ───────────────────────────────────────────────────────

const inputStyle = {
  width: "100%",
  fontSize: "12px",
  borderRadius: "6px",
  border: "1px solid #d1d5db",
  padding: "6px 10px",
  color: "#111827",
  background: "#fff",
};

// ─── Main Filter Component ────────────────────────────────────────────────────

/**
 * MonsterBooleanSearchFilter
 *
 * Props:
 *  collapsed      - boolean
 *  onToggleCollapse - () => void
 *  onNewSearch    - () => void
 *  onClear        - () => void
 *  onApply        - (filters) => void
 *  resetKey       - number (increment to reset)
 *
 */
const stateZipMap: any = {
  AL: "36101", // Alabama
  AK: "99501", // Alaska
  AS: "96799", // American Samoa
  AZ: "85001", // Arizona
  AR: "72201", // Arkansas
  AE: "09001", // Armed Forces Europe/Middle East/Canada
  AA: "34002", // Armed Forces Americas
  AP: "96201", // Armed Forces Pacific
  CA: "90001", // California
  CO: "80001", // Colorado
  CT: "06101", // Connecticut
  DE: "19901", // Delaware
  DC: "20001", // District of Columbia
  FM: "96941", // Federated States of Micronesia
  FL: "32004", // Florida
  GA: "30301", // Georgia
  GU: "96910", // Guam
  HI: "96701", // Hawaii
  ID: "83201", // Idaho
  IL: "60007", // Illinois
  IN: "46201", // Indiana
  IA: "50001", // Iowa
  KS: "66002", // Kansas
  KY: "40003", // Kentucky
  LA: "70001", // Louisiana
  ME: "03901", // Maine
  MD: "20601", // Maryland
  MA: "01001", // Massachusetts
  MI: "48001", // Michigan
  MN: "55001", // Minnesota
  MS: "38601", // Mississippi
  MO: "63001", // Missouri
  MT: "59001", // Montana
  NE: "68001", // Nebraska
  NV: "88901", // Nevada
  NH: "03031", // New Hampshire
  NJ: "07001", // New Jersey
  NM: "87001", // New Mexico
  NY: "10001", // New York
  NC: "27006", // North Carolina
  ND: "58001", // North Dakota
  MP: "96950", // Northern Mariana Islands
  OH: "43001", // Ohio
  OK: "73001", // Oklahoma
  OR: "97001", // Oregon
  PA: "15001", // Pennsylvania
  PR: "00601", // Puerto Rico
  RI: "02801", // Rhode Island
  SC: "29001", // South Carolina
  SD: "57001", // South Dakota
  TN: "37010", // Tennessee
  TX: "75001", // Texas
  UT: "84001", // Utah
  VT: "05001", // Vermont
  VI: "00801", // Virgin Islands
  VA: "20101", // Virginia
  WA: "98001", // Washington
  WV: "24701", // West Virginia
  WI: "53001", // Wisconsin
  WY: "82001", // Wyoming
};



const stateAbbrToZip: any = {
  AL: "36101",
  AK: "99501",
  AZ: "85001",
  AR: "72201",
  CA: "90001",
  CO: "80001",
  CT: "06101",
  DE: "19901",
  DC: "20001",
  FL: "32004",
  GA: "30301",
  HI: "96701",
  ID: "83201",
  IL: "60007",
  IN: "46201",
  IA: "50001",
  KS: "66002",
  KY: "40003",
  LA: "70001",
  ME: "03901",
  MD: "20601",
  MA: "01001",
  MI: "48001",
  MN: "55001",
  MS: "38601",
  MO: "63001",
  MT: "59001",
  NE: "68001",
  NV: "88901",
  NH: "03031",
  NJ: "07001",
  NM: "87001",
  NY: "10001",
  NC: "27006",
  ND: "58001",
  OH: "43001",
  OK: "73001",
  OR: "97001",
  PA: "15001",
  RI: "02801",
  SC: "29001",
  SD: "57001",
  TN: "37010",
  TX: "75001",
  UT: "84001",
  VT: "05001",
  VA: "20101",
  WA: "98001",
  WV: "24701",
  WI: "53001",
  WY: "82001",
  PR: "00601",
  GU: "96910",
};

export const MonsterBooleanSearchFilter = ({ collapsed, onToggleCollapse, onNewSearch, onClear, onApply, resetKey = 0 }: any) => {
  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS });
  const [errors, setErrors] = useState({} as any);
  const [suggestions, setSuggestions] = useState<any[]>([]); // ← ADD

  const searchLocation = (event: { query: string }) => {
  const query = event.query.trim().toLowerCase();
  if (!query) { setSuggestions([]); return; }

  const filtered = stateWithCity?.filter(
    (item: any) =>
      item.State.toLowerCase().includes(query) ||
      item.City.toLowerCase().includes(query)
  ).map((item: any) => {
    const keyFull = item.State.replace(/\s/g, "");          // "Texas"
    const keyAbbr = item.State.trim().toUpperCase();         // "TX"
    const zipCode = stateZipMap[keyFull] ?? stateAbbrToZip[keyAbbr];
    return { ...item, zipCode };
  });

  setSuggestions((filtered ?? []).slice(0, 50));
};
  // Reset when resetKey changes
  useEffect(() => {
    setFilters({ ...DEFAULT_FILTERS });
    setErrors({});
  }, [resetKey]);

  const set = (key: any, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    if ((errors as any)[key]) setErrors((prev: any) => ({ ...prev, [key]: undefined }));
  };

  // ── Validation ──────────────────────────────────────────────────────────────
  const validate = () => {
    const errs = {} as any;

    if (filters.resumeUpdatedRange === "custom" && !filters.resumeUpdatedFrom) {
      errs.resumeUpdatedFrom = "From date is required";
    }

    if (filters.salaryMin && filters.salaryMax) {
      if (Number(filters.salaryMin) > Number(filters.salaryMax)) {
        errs.salaryMax = "Max salary must be greater than Min salary";
      }
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };
  const handleApply = () => {
    if (validate()) {
      onApply && onApply(filters);
    }
  };

  const handleClear = () => {
    setFilters({ ...DEFAULT_FILTERS });
    setErrors({});
    onClear && onClear();
  };

  // ── Collapsed state ─────────────────────────────────────────────────────────
  if (collapsed) {
    return (
      <div
        style={{
          width: "32px",
          display: "flex",
          alignItems: "flex-start",
          paddingTop: "12px",
          borderRight: "1px solid #e5e7eb",
          backgroundColor: "#fff",
          flexShrink: 0,
          height: "100vh",
        }}
      >
        <Button icon="pi pi-chevron-right" className="p-button-text p-button-sm p-0" style={{ color: "#6b7280", width: "32px" }} onClick={onToggleCollapse} tooltip="Expand filters" tooltipOptions={{ position: "right" }} />
      </div>
    );
  }

  // ── Expanded state ──────────────────────────────────────────────────────────
  return (
    <div
      style={{
        width: "310px",
        minWidth: "310px",
        borderRight: "1px solid #e5e7eb",
        backgroundColor: "#fff",
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        overflow: "hidden",
        transition: "width 0.25s ease",
      }}
    >
      {/* ── Header ────────────────────────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 16px",
          borderBottom: "1px solid #e5e7eb",
          background: "linear-gradient(135deg, #f5f0ff, #faf8ff)",
          flexShrink: 0,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <i className="pi pi-sliders-h" style={{ color: "#9333ea", fontSize: "14px" }} />
          <span style={{ fontSize: "13px", fontWeight: 700, color: "#4b0082" }}>Refine Search</span>
        </div>
        <Button icon="pi pi-chevron-left" className="p-button-text p-button-sm p-0" style={{ color: "#6b7280" }} onClick={onToggleCollapse} />
      </div>

      {/* ── Scrollable Body ────────────────────────────────────────────────── */}
      <div style={{ flex: 1, overflowY: "auto", padding: "0 0 12px 0" }}>
        <Accordion multiple activeIndex={[0, 1, 2, 3, 4, 5]}>
          {/* ── 1. Keywords & Location ───────────────────────────────────── */}
          <AccordionTab
            header={
              <span style={{ fontSize: "12px", fontWeight: 700, color: "#4b0082" }}>
                <i className="pi pi-search" style={{ marginRight: 6, color: "#9333ea" }} />
                Keywords & Location
              </span>
            }
          >
            {/* <FieldWrapper label="Job Title">
                            <InputText
                                value={filters.jobTitle}
                                onChange={(e) => set("jobTitle", e.target.value)}
                                placeholder="e.g. Software Engineer"
                                style={inputStyle}
                            />
                        </FieldWrapper> */}

            <FieldWrapper label="Location (City or State)">
              <AutoComplete
                value={filters.location}
                suggestions={suggestions}
                completeMethod={searchLocation}
                field="City"
                onChange={(e) => set("location", e.value)}
                multiple
                itemTemplate={(item: any) => (
                  <div style={{ fontSize: "12px" }}>
                    <strong>{item.City}</strong>, {item.State}
                  </div>
                )}
                placeholder="Search City or State..."
                style={{ width: "100%" }}
                inputStyle={{ ...inputStyle, width: "100%" }}
              />
            </FieldWrapper>

            <FieldWrapper label="Search Radius">
              <Dropdown value={filters.radius} options={RADIUS_OPTIONS} onChange={(e) => set("radius", e.value)} placeholder="Select radius" style={{ ...inputStyle, padding: "3px 6px" }} />
            </FieldWrapper>

            <FieldWrapper label="Skill Keywords">
              <InputText value={filters.skillKeywords} onChange={(e) => set("skillKeywords", e.target.value)} placeholder="e.g. React, Python, SQL" style={inputStyle} />
              <span style={{ fontSize: "10px", color: "#9ca3af", marginTop: 2, display: "block" }}>Comma or space separated</span>
            </FieldWrapper>
          </AccordionTab>

          {/* ── 2. Resume Updated ────────────────────────────────────────── */}
          <AccordionTab
            header={
              <span style={{ fontSize: "12px", fontWeight: 700, color: "#4b0082" }}>
                <i className="pi pi-calendar" style={{ marginRight: 6, color: "#9333ea" }} />
                Resume Updated
              </span>
            }
          >
            <FieldWrapper label="Updated Within">
              <Dropdown value={filters.resumeUpdatedRange} options={RESUME_UPDATED_OPTIONS} onChange={(e) => set("resumeUpdatedRange", e.value)} placeholder="Any time" style={{ ...inputStyle, padding: "3px 6px" }} />
            </FieldWrapper>

            {filters.resumeUpdatedRange === "custom" && (
              <>
                <FieldWrapper label="Updated Since (From Date)" error={errors.resumeUpdatedFrom}>
                  <Calendar value={filters.resumeUpdatedFrom} onChange={(e) => set("resumeUpdatedFrom", e.value)} placeholder="MM/DD/YYYY" showIcon dateFormat="mm/dd/yy" maxDate={filters.resumeUpdatedTo || new Date()} inputStyle={{ ...inputStyle, width: "100%" }} style={{ width: "100%" }} />
                </FieldWrapper>
              </>
            )}
          </AccordionTab>

          {/* ── 3. Candidate Preferences ─────────────────────────────────── */}
          <AccordionTab
            header={
              <span style={{ fontSize: "12px", fontWeight: 700, color: "#4b0082" }}>
                <i className="pi pi-user" style={{ marginRight: 6, color: "#9333ea" }} />
                Candidate Preferences
              </span>
            }
          >
            <FieldWrapper label="Relocation">
              <Dropdown value={filters.relocation} options={RELOCATION_OPTIONS} onChange={(e) => set("relocation", e.value)} placeholder="Any" style={{ ...inputStyle, padding: "3px 6px" }} />
            </FieldWrapper>

            <FieldWrapper label="Work Authorization">
              <Dropdown value={filters.workAuth} options={WORK_AUTH_OPTIONS} onChange={(e) => set("workAuth", e.value)} placeholder="Any" style={{ ...inputStyle, padding: "3px 6px" }} />
            </FieldWrapper>

            <FieldWrapper label="Education Level">
              <Dropdown value={filters.educationLevel} options={EDUCATION_LEVELS} onChange={(e) => set("educationLevel", e.value)} placeholder="Any" style={{ ...inputStyle, padding: "3px 6px" }} />
            </FieldWrapper>

            <FieldWrapper label="Military Status">
              <Dropdown value={filters.militaryExperience} options={MILITARY_OPTIONS} onChange={(e) => set("militaryExperience", e.value)} placeholder="Any" style={{ ...inputStyle, padding: "3px 6px" }} />
            </FieldWrapper>
          </AccordionTab>

          {/* ── 4. Most Recent Experience ─────────────────────────────────── */}
          <AccordionTab
            header={
              <span style={{ fontSize: "12px", fontWeight: 700, color: "#4b0082" }}>
                <i className="pi pi-briefcase" style={{ marginRight: 6, color: "#9333ea" }} />
                Most Recent Experience
              </span>
            }
          >
            <FieldWrapper label="Most Recent Employer">
              <InputText value={filters.mostRecentEmployer} onChange={(e) => set("mostRecentEmployer", e.target.value)} placeholder="e.g. Google, Amazon" style={inputStyle} />
            </FieldWrapper>

            <FieldWrapper label="Most Recent Job Title">
              <InputText value={filters.mostRecentJobTitle} onChange={(e) => set("mostRecentJobTitle", e.target.value)} placeholder="e.g. Senior Developer" style={inputStyle} />
            </FieldWrapper>

            <FieldWrapper label="Most Recent Job Description">
              <textarea
                value={filters.mostRecentJobDescription}
                onChange={(e) => set("mostRecentJobDescription", e.target.value)}
                placeholder="Keywords from job description..."
                rows={3}
                style={{
                  ...inputStyle,
                  resize: "vertical",
                  fontFamily: "inherit",
                  lineHeight: "1.5",
                }}
              />
            </FieldWrapper>
          </AccordionTab>

          {/* ── 5. Education ─────────────────────────────────────────────── */}
          <AccordionTab
            header={
              <span style={{ fontSize: "12px", fontWeight: 700, color: "#4b0082" }}>
                <i className="pi pi-book" style={{ marginRight: 6, color: "#9333ea" }} />
                Education
              </span>
            }
          >
            <FieldWrapper label="School Attended">
              <InputText value={filters.schoolAttended} onChange={(e) => set("schoolAttended", e.target.value)} placeholder="e.g. MIT, Stanford" style={inputStyle} />
            </FieldWrapper>
          </AccordionTab>

          {/* ── 6. Salary ────────────────────────────────────────────────── */}
          {/* <AccordionTab
                        header={
                            <span style={{ fontSize: "12px", fontWeight: 700, color: "#4b0082" }}>
                                <i className="pi pi-dollar" style={{ marginRight: 6, color: "#9333ea" }} />
                                Salary
                            </span>
                        }
                    >
                        <FieldWrapper label="Salary Type">
                            <Dropdown
                                value={filters.salaryType}
                                options={SALARY_TYPE_OPTIONS}
                                onChange={(e) => set("salaryType", e.value)}
                                placeholder="Any"
                                style={{ ...inputStyle, padding: "3px 6px" }}
                            />
                        </FieldWrapper>

                        <div style={{ display: "flex", gap: "8px" }}>
                            <FieldWrapper label="Min ($)" error={null}>
                                <InputText
                                    value={filters.salaryMin}
                                    onChange={(e) => set("salaryMin", e.target.value.replace(/\D/g, ""))}
                                    placeholder="0"
                                    style={{ ...inputStyle, width: "100%" }}
                                />
                            </FieldWrapper>
                            <FieldWrapper label="Max ($)" error={errors.salaryMax}>
                                <InputText
                                    value={filters.salaryMax}
                                    onChange={(e) => set("salaryMax", e.target.value.replace(/\D/g, ""))}
                                    placeholder="0"
                                    style={{ ...inputStyle, width: "100%", borderColor: errors.salaryMax ? "#dc2626" : "#d1d5db" }}
                                />
                            </FieldWrapper>
                        </div>
                        {errors.salaryMax && (
                            <span style={{ fontSize: "10px", color: "#dc2626", display: "block", marginTop: "-8px", marginBottom: "8px" }}>
                                <i className="pi pi-exclamation-circle" style={{ marginRight: 3 }} />
                                {errors.salaryMax}
                            </span>
                        )}
                    </AccordionTab> */}
        </Accordion>
      </div>

      {/* ── Footer Buttons ─────────────────────────────────────────────────── */}
      <div
        style={{
          borderTop: "1px solid #e5e7eb",
          padding: "12px 16px",
          display: "flex",
          gap: "8px",
          flexShrink: 0,
          background: "#fafafa",
        }}
      >
        <Button label="NEW SEARCH" className="p-button-outlined p-button-sm" style={{ flex: 1, fontSize: "11px", color: "#6b7280", borderColor: "#d1d5db" }} onClick={onNewSearch} />
        <Button label="CLEAR" className="p-button-outlined p-button-sm" style={{ flex: 1, fontSize: "11px", color: "#6b7280", borderColor: "#d1d5db" }} onClick={handleClear} />
        <Button label="UPDATE" className="p-button-sm" style={{ flex: 1, fontSize: "12px", backgroundColor: "#374151", border: "none" }} onClick={handleApply} />
      </div>
    </div>
  );
};

export default MonsterBooleanSearchFilter;
export { DEFAULT_FILTERS };
